import type { ObtainMethod, QuoteFile } from '@frontend/api';

export type NodeData = {
  parentUuid: string;
  childUuid: string;
  count: number;
  position: number;
};

export type BomPartFile = Pick<
  QuoteFile,
  | 'uuid'
  | 'filename'
  | 'isExportControlled'
  | 'category'
  | 'uploaded'
  | 'fileSize'
  | 'fileType'
  | 'promotedInId'
  | 'isAssembly'
> & {
  assemblyParentFileId: string | null;
};

export type BomBuilderSuggestionState = 'accepted' | 'rejected' | undefined;

export type BomPart = {
  uuid: string;
  primaryFile: BomPartFile | null;
  isRootPart: boolean;
  supportingFiles: BomPartFile[];
  isAssembly: boolean;
  obtainMethod: ObtainMethod;
  purchasedComponentId: number | null;
  partNumber: string | null;
  revision: string | null;
  description: string | null;
};

export const SUGGESTABLE_PART_FIELDS = [
  'partNumber',
  'isAssembly',
  'revision',
  'description',
  'obtainMethod',
  'purchasedComponentId',
] as const satisfies readonly (keyof BomPart)[];

export const SUGGESTABLE_PART_FIELDS_FOR_PART_DATA = [
  'partNumber',
  'revision',
  'description',
  'obtainMethod',
  'purchasedComponentId',
] as const satisfies readonly (keyof BomPart)[];

export const GOOD_PDF_PART_FIELDS = [
  'partNumber',
  'revision',
  'description',
] as const satisfies readonly (keyof BomPart)[];

export type SuggestablePartField = (typeof SUGGESTABLE_PART_FIELDS)[number];

export const STRUCTURE_SUGGESTION = 'STRUCTURE';
export type SuggestableStructureKey = typeof STRUCTURE_SUGGESTION;

export const SUGGESTABLE_NODE_FIELDS = [
  'count',
] as const satisfies readonly (keyof NodeData)[];

export type SuggestableNodeField = (typeof SUGGESTABLE_NODE_FIELDS)[number];

export const SUGGESTABLE_FIELD = [
  ...SUGGESTABLE_PART_FIELDS,
  ...SUGGESTABLE_NODE_FIELDS,
] as const;

export type SuggestableField = (typeof SUGGESTABLE_FIELD)[number];

export type SuggestedField = SuggestableField | SuggestableStructureKey;

export type NodeKey = `${NodeData['parentUuid']},${NodeData['childUuid']}`;
export type BomStructure = {
  partMap: Record<BomPart['uuid'], BomPart>;
  nodeMap: Record<NodeKey, NodeData>;
};

export type ExtractedTableMeta = {
  extractedTableId: number;
  printPageId?: number;
  pageNumber?: number;
  spreadsheetTabId?: number;
  partFileUuid: BomPartFile['uuid'];
};

export type BomStructureFileSuggestion = {
  // file uuid
  uuid: BomPartFile['uuid'];
  // this contains "truth" structure - hydrated directly from model with 100% confidence
  structure: BomStructure;
  // this contains suggested BOMs from extracted tables in spreadsheets and pdf files
  suggestions: Array<
    {
      structure: BomStructure;
    } & ExtractedTableMeta
  >;
};

// do we need source key in this? or will we only ever provide one suggestion?
export type SuggestionDest = BomPart['uuid'] | NodeKey;
export type SuggestionSource =
  | BomPartFile['uuid']
  | ExtractedTableMeta['extractedTableId'];
export type SuggestionKey =
  `${SuggestionDest},${SuggestedField},${SuggestionSource}`;
export type SuggestionStatusMap = Record<
  SuggestionKey,
  BomBuilderSuggestionState
>;

export type FieldSuggestionValue<Value = string | number | boolean> = {
  value: Value;
  sourceKey: BomPartFile['uuid'];
};

export type FileSuggestionValue = {
  value: BomPartFile;
  sourceKey: BomPartFile['uuid'];
  sourceMeta:
    | {
        assemblySourceFile: BomPartFile;
        sourceDestination: 'ASSEMBLY_CAD';
      }
    | {
        assemblySourceFile: null;
        sourceDestination: 'QUOTE_FILES';
      };
};

export type ExtractedTableSuggestionValue = {
  value: {
    partUpdates: Partial<BomPart>;
    structure: BomStructure;
  };
  sourceKey: ExtractedTableMeta['extractedTableId'];
  sourceMeta: ExtractedTableMeta;
};

export type SuggestionValue =
  | FieldSuggestionValue
  | FileSuggestionValue
  | ExtractedTableSuggestionValue;

export const getIsExtractedTableSuggestionValue = (
  suggestionValue: SuggestionValue,
): suggestionValue is ExtractedTableSuggestionValue =>
  'sourceMeta' in suggestionValue &&
  suggestionValue.sourceMeta &&
  'extractedTableId' in suggestionValue.sourceMeta;

export type SuggestionValueMap = {
  [PartField in SuggestablePartField]: FieldSuggestionValue[];
} & {
  [NodeField in SuggestableNodeField]: FieldSuggestionValue[];
} & {
  [StructureField in SuggestableStructureKey]: ExtractedTableSuggestionValue[];
};

export const getEmptySuggestionValueMap = (): SuggestionValueMap => ({
  partNumber: [],
  isAssembly: [],
  obtainMethod: [],
  revision: [],
  description: [],
  count: [],
  purchasedComponentId: [],
  STRUCTURE: [],
});

/**
 * Provide nested object suggestions that enable you to on the fly construct
 * the SuggestionKey values by combining destination, field, and source
 */
export type Suggestions = Record<BomPart['uuid'] | NodeKey, SuggestionValueMap>;

export type BomBuilderState = BomStructure & {
  suggestions?: Suggestions;
  suggestionStatusMap?: SuggestionStatusMap;
  suggestionPreviewState?: {
    activeSuggestionKey: SuggestionKey;
    suggestionPreviewEdgeIds: NodeKey[];
    oldPartDataForSuggestionPreview: BomPart;
    oldSuggestionStatusMap: SuggestionStatusMap;
    rowIdForSuggestedTableAlert: string;
    fieldSuggestionKeysToAccept: SuggestionKey[];
  } | null;
};

// frontend state for BOM Builder
export type BomBuilderStructureState = Required<BomBuilderState>;

// other helper functions
export const getIsSuggestablePartField = (
  field: string,
): field is SuggestablePartField =>
  SUGGESTABLE_PART_FIELDS.includes(field as SuggestablePartField);

export const getIsSuggestableNodeField = (
  field: string,
): field is SuggestableNodeField =>
  SUGGESTABLE_NODE_FIELDS.includes(field as SuggestableNodeField);
